import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { X, Plus, AlertCircle, Loader2 } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

/**
 * Modal minimo para crear una tarea desde cualquier sub-vista del proyecto.
 *
 * El boton "Nueva tarea" del header del proyecto lo dispara — antes ese boton
 * solo funcionaba en el board (via CustomEvent) y no hacia nada en Lista,
 * Calendario, Gantt o Configuracion.
 *
 * Defaults: estado = primer estado de la categoria 'por_hacer' del proyecto.
 * Para detalles (asignado, prioridad, fecha), el usuario edita en el slide-over
 * tras crear.
 */
const QuickAddTaskModal = ({ isOpen, proyecto, onClose, onCreated }) => {
  const [titulo, setTitulo] = useState('');
  const [estadoId, setEstadoId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Estados ordenados por orden, agrupados por categoria. Para el select.
  const estadosPorCategoria = useMemo(() => {
    const map = {};
    (proyecto?.estados || []).forEach((e) => {
      if (!map[e.categoria]) map[e.categoria] = [];
      map[e.categoria].push(e);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.orden - b.orden));
    return map;
  }, [proyecto?.estados]);

  // Default: primer por_hacer. Fallback al primero del proyecto si no existiera.
  const defaultEstadoId = useMemo(() => {
    const porHacer = estadosPorCategoria.por_hacer?.[0];
    if (porHacer) return porHacer.id;
    return (proyecto?.estados || [])[0]?.id || null;
  }, [estadosPorCategoria, proyecto?.estados]);

  // Reset al abrir y al cerrar (asi reabrir con otro proyecto no arrastra estado).
  useEffect(() => {
    if (isOpen) {
      setTitulo('');
      setEstadoId(defaultEstadoId);
      setError('');
      setSubmitting(false);
    }
  }, [isOpen, defaultEstadoId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();
    setError('');
    const t = titulo.trim();
    if (!t) {
      setError('Ponle un título a la tarea');
      return;
    }
    if (!proyecto?.id) return;
    setSubmitting(true);
    try {
      const { data } = await axiosInstance.post(
        `/api/proyectos/${proyecto.id}/tareas`,
        {
          titulo: t,
          estado_id: estadoId || undefined,
        }
      );
      onCreated?.(data);
    } catch (err) {
      console.error('Error creando tarea:', err);
      setError(err.response?.data?.error || 'No pudimos crear la tarea.');
      setSubmitting(false);
    }
  }, [titulo, estadoId, proyecto?.id, onCreated]);

  if (!isOpen) return null;

  const modal = (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.12 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      >
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 380, damping: 28 }}
          className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Acento superior con color del proyecto */}
          <div
            className="h-1 w-full"
            style={{ backgroundColor: proyecto?.color || '#06b6d4' }}
          />

          <form onSubmit={handleSubmit}>
            <div className="flex items-start justify-between px-5 pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Plus size={14} className="text-slate-500" />
                <h2 className="text-sm font-semibold text-slate-900 tracking-tight">
                  Nueva tarea
                </h2>
                {proyecto?.slug && (
                  <span className="font-mono text-[10px] tracking-widest text-slate-400 uppercase">
                    {proyecto.slug}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 p-1 rounded transition-colors disabled:opacity-50"
              >
                <X size={14} />
              </button>
            </div>

            <div className="px-5 pb-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Título <span className="text-rose-500">*</span>
                </label>
                <textarea
                  autoFocus
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  placeholder="¿Qué hay que hacer?"
                  rows={2}
                  maxLength={200}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-colors resize-none"
                />
              </div>

              {(proyecto?.estados || []).length > 1 && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Estado inicial
                  </label>
                  <select
                    value={estadoId || ''}
                    onChange={(e) => setEstadoId(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-colors"
                  >
                    {Object.entries(estadosPorCategoria).map(([cat, estados]) => (
                      <optgroup key={cat} label={cat.replace('_', ' ')}>
                        {estados.map((est) => (
                          <option key={est.id} value={est.id}>
                            {est.nombre}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-1.5 px-2.5 py-1.5 bg-rose-50 border border-rose-200/70 rounded text-[11px] text-rose-700">
                  <AlertCircle size={12} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-5 py-3 bg-slate-50/60 border-t border-slate-200/70">
              <span className="text-[10px] text-slate-400">
                <kbd className="px-1 text-[9px] font-mono bg-white text-slate-500 border border-slate-200 rounded">⌘↵</kbd> guarda
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting || !titulo.trim()}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-md transition-colors active:scale-[0.98] disabled:opacity-50"
                >
                  {submitting && <Loader2 size={12} className="animate-spin" />}
                  {submitting ? 'Creando...' : 'Crear tarea'}
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modal, document.body);
};

export default QuickAddTaskModal;
